import { FilterEntityType, FilterOperator } from './filters.types'

export enum FilterProperty {
  Project = 'project',
  Workflow = 'workflow',
  ProjectType = 'project-type',
  PrivateProject = 'private-project',
  ProjectStatus = 'project-status',
  ProjectStatusType = 'project-status-type',
  ProjectClient = 'project-client',
  ClientTag = 'client-tag',
  ProjectTeam = 'project-team',
  ProjectTag = 'project-tag',
  ProjectStartDate = 'project-start-date',
  ProjectDueDate = 'project-due-date',
  ProjectCreatedDate = 'project-created-date',
  TaskCreatedDate = 'task-created-date',
  User = 'user',
  UserAssigned = 'user-assigned',
  UserTeam = 'user-team',
  UserTag = 'user-tag',
  StartDate = 'start-date',
  DueDate = 'due-date',
  Task = 'task',
  TaskIdentifier = 'task-identifier',
  TypeOfWork = 'type-of-work',
  TaskStatus = 'task-status',
  TaskStatusType = 'task-status-type',
  TaskTag = 'task-tag',
  TaskList = 'task-list',
  TaskCreator = 'task-creator',
  TaskIsPrio = 'task-is-prio',
  TaskIsSubtask = 'task-is-subtask',
  ProjectName = 'project-name',
  ProjectKey = 'project-key',
  UserResponsible = 'user-responsible',
  ProjectMembers = 'project-members',
  UserFirstName = 'user-first-name',
  UserLastName = 'user-last-name',
  UserEmail = 'user-email',
  UserPosition = 'user-position',
  UserStatus = 'user-status',
  Company = 'company',
  CompanyAddress = 'company-address',
  IsExternal = 'is-external',
  // Custom field properties
  CustomFieldCheckbox = 'custom-field-checkbox',
  CustomFieldLink = 'custom-field-link',
  CustomFieldNumber = 'custom-field-number',
  CustomFieldSelection = 'custom-field-selection',
  CustomFieldUser = 'custom-field-user',
  CustomFieldClient = 'custom-field-client',
  CustomFieldText = 'custom-field-text',
  CustomFieldDate = 'custom-field-date',
  CustomFieldDateTime = 'custom-field-date-time',
  // AI prompt option
  Prompt = 'ai-prompt'
}

export enum FilterExpectedType {
  None = 'none',
  String = 'string',
  Number = 'number',
  Boolean = 'boolean',
  Date = 'date',
  DateRange = 'date-range',
  DateTime = 'date-time',
  DateTimeRange = 'date-time-range',
  Company = 'company',
  CompanyTag = 'company-tag',
  Project = 'project',
  ProjectStatus = 'project-status',
  ProjectStatusType = 'project-status-type',
  ProjectTag = 'project-tag',
  ProjectType = 'project-type',
  Task = 'task',
  TaskTag = 'task-tag',
  TaskStatusType = 'task-status-type',
  Team = 'team',
  TypeOfWork = 'type-of-work',
  User = 'user',
  UserStatus = 'user-status',
  UserTag = 'user-tag',
  Workflow = 'workflow',
  CustomFieldSelection = 'custom-field-selection'
}

type FilterOperatorsWithExpectedType = Partial<Record<FilterOperator, FilterExpectedType>>

type FilterComputedValueId = 'Me' | 'Now' | 'Today' | 'StartOfWeek' | 'EndOfWeek' | 'StartOfMonth' | 'EndOfMonth'

export interface FilterComputedValueOption {
  value: FilterComputedValueId
  allowDaysOffset?: boolean
}

export type FilterComputedValuesByOperator = Partial<Record<FilterOperator, FilterComputedValueOption[]>>

export interface FilterPropertyConfiguration {
  property: FilterProperty
  expectedType: FilterOperatorsWithExpectedType
  path: string
  idPath?: string
  isCollection?: boolean
  allowMultipleValues?: boolean
  relatedEntity?: FilterPropertyEntity
  isCompact?: boolean
  computedValues?: FilterComputedValuesByOperator
}

export enum FilterPropertyEntity {
  Project = 'project',
  Task = 'task',
  User = 'user',
  Client = 'client',
  TaskCustomField = 'taskCustomField',
  ProjectCustomField = 'projectCustomField'
}

/**
 * Creates an expected type configuration for specific properties
 * @param {FilterExpectedType} type - The expected type to create configuration for
 * @returns {FilterOperatorsWithExpectedType} Object mapping FilterOperators to the provided ExpectedType
 */
export function getPropertyExpectedType(type: FilterExpectedType): FilterOperatorsWithExpectedType {
  return {
    [FilterOperator.Equal]: type,
    [FilterOperator.NotEqual]: type
  }
}

/**
 * Creates an expected type configuration for specific collection properties
 * @param {FilterExpectedType} type - The expected type to create configuration for
 * @returns {FilterOperatorsWithExpectedType} Object mapping FilterOperators to the provided ExpectedType
 */
export function getCollectionPropertyExpectedType(type: FilterExpectedType): FilterOperatorsWithExpectedType {
  return {
    [FilterOperator.Equal]: type,
    [FilterOperator.NotEqual]: type
  }
}

/**
 * Expected types for string based operators
 */
const STRING_PROPERTY_FILTER_EXPECTED_TYPE = {
  [FilterOperator.Contains]: FilterExpectedType.String,
  [FilterOperator.DoesNotContain]: FilterExpectedType.String
}

/**
 * Expected types for is/is-not set operators
 */
const FLAG_PROPERTY_FILTER_EXPECTED_TYPE = {
  [FilterOperator.IsSet]: FilterExpectedType.None,
  [FilterOperator.IsNotSet]: FilterExpectedType.None
}

/**
 * Expected types for date based operators
 */
const DATE_PROPERTY_FILTER_EXPECTED_TYPE = {
  [FilterOperator.Equal]: FilterExpectedType.Date,
  [FilterOperator.NotEqual]: FilterExpectedType.Date,
  [FilterOperator.LessThan]: FilterExpectedType.Date,
  [FilterOperator.Between]: FilterExpectedType.DateRange,
  [FilterOperator.GreaterThan]: FilterExpectedType.Date,
  [FilterOperator.WithinThisWeek]: FilterExpectedType.None,
  [FilterOperator.WithinThisMonth]: FilterExpectedType.None,
  ...FLAG_PROPERTY_FILTER_EXPECTED_TYPE
}

const CREATION_DATE_PROPERTY_FILTER_EXPECTED_TYPE = {
  ...DATE_PROPERTY_FILTER_EXPECTED_TYPE,
  [FilterOperator.WithinLastWeek]: FilterExpectedType.None,
  [FilterOperator.WithinLastMonth]: FilterExpectedType.None
}

/**
 * Expected types for date based operators
 */
const DATE_TIME_PROPERTY_FILTER_EXPECTED_TYPE = {
  [FilterOperator.Equal]: FilterExpectedType.Date,
  [FilterOperator.NotEqual]: FilterExpectedType.Date,
  [FilterOperator.LessThan]: FilterExpectedType.DateTime,
  [FilterOperator.Between]: FilterExpectedType.DateTimeRange,
  [FilterOperator.GreaterThan]: FilterExpectedType.DateTime,
  [FilterOperator.WithinThisWeek]: FilterExpectedType.None,
  [FilterOperator.WithinThisMonth]: FilterExpectedType.None,
  ...FLAG_PROPERTY_FILTER_EXPECTED_TYPE
}

const USER_COMPUTED_VALUES: FilterComputedValueOption[] = [{ value: 'Me' }]

const DATE_COMPUTED_VALUES: FilterComputedValueOption[] = [
  { value: 'Now' },
  { value: 'Today', allowDaysOffset: true },
  { value: 'StartOfWeek' },
  { value: 'EndOfWeek' },
  { value: 'StartOfMonth' },
  { value: 'EndOfMonth' }
]

const DATE_EQUALITY_COMPUTED_VALUES: FilterComputedValueOption[] = [
  { value: 'Today', allowDaysOffset: true },
  { value: 'StartOfWeek' },
  { value: 'EndOfWeek' },
  { value: 'StartOfMonth' },
  { value: 'EndOfMonth' }
]

/**
 * Maps each operator to its supported computed values.
 * @param {FilterOperator[]} operators Operators that share the computed values.
 * @param {FilterComputedValueOption[]} computedValues Supported computed values.
 * @returns {FilterComputedValuesByOperator} Computed values for each operator.
 */
function getComputedValuesForOperators(
  operators: FilterOperator[],
  computedValues: FilterComputedValueOption[]
): FilterComputedValuesByOperator {
  return operators.reduce<FilterComputedValuesByOperator>((acc, operator) => {
    acc[operator] = computedValues
    return acc
  }, {})
}

const USER_COMPUTED_VALUES_BY_OPERATOR = getComputedValuesForOperators(
  [FilterOperator.Equal, FilterOperator.NotEqual, FilterOperator.Contains, FilterOperator.DoesNotContain],
  USER_COMPUTED_VALUES
)

const DATE_COMPUTED_VALUES_BY_OPERATOR = {
  ...getComputedValuesForOperators([FilterOperator.Equal, FilterOperator.NotEqual], DATE_EQUALITY_COMPUTED_VALUES),
  ...getComputedValuesForOperators(
    [FilterOperator.LessThan, FilterOperator.GreaterThan, FilterOperator.Between],
    DATE_COMPUTED_VALUES
  )
}

/**
 * Expected types for number based operators
 */
const NUMBER_PROPERTY_FILTER_EXPECTED_TYPE = {
  [FilterOperator.Equal]: FilterExpectedType.Number,
  [FilterOperator.NotEqual]: FilterExpectedType.Number,
  [FilterOperator.LessThan]: FilterExpectedType.Number,
  [FilterOperator.LessThanOrEqual]: FilterExpectedType.Number,
  [FilterOperator.GreaterThan]: FilterExpectedType.Number,
  [FilterOperator.GreaterThanOrEqual]: FilterExpectedType.Number,
  ...FLAG_PROPERTY_FILTER_EXPECTED_TYPE
}

/**
 * Expected types for boolean based operators
 */
const BOOLEAN_PROPERTY_FILTER_EXPECTED_TYPE = {
  [FilterOperator.Equal]: FilterExpectedType.Boolean,
  [FilterOperator.NotEqual]: FilterExpectedType.Boolean
}

/**
 * Expected types for custom field selection properties
 */
const SELECTION_FILTER_EXPECTED_TYPE = {
  [FilterOperator.Equal]: FilterExpectedType.CustomFieldSelection,
  [FilterOperator.NotEqual]: FilterExpectedType.CustomFieldSelection,
  ...FLAG_PROPERTY_FILTER_EXPECTED_TYPE
}

/**
 * Creates an expected type configuration for required properties
 * Required properties do not support 'is set' and 'is not set' operators
 * @param {FilterExpectedType} type - The expected type to create configuration for
 * @returns {FilterOperatorsWithExpectedType} Object mapping FilterOperators to the provided ExpectedType and string operators
 */
export function getRequiredPropertyExpectedType(type: FilterExpectedType): FilterOperatorsWithExpectedType {
  return {
    ...getPropertyExpectedType(type),
    ...STRING_PROPERTY_FILTER_EXPECTED_TYPE
  }
}

/**
 * Creates an expected type configuration for required collection properties
 * Required collection properties do not support 'is set' and 'is not set' operators
 * @param {FilterExpectedType} type - The expected type to create configuration for
 * @returns {FilterOperatorsWithExpectedType} Object mapping FilterOperators to the provided ExpectedType and string operators
 */
export function getRequiredCollectionPropertyExpectedType(type: FilterExpectedType): FilterOperatorsWithExpectedType {
  return {
    ...getCollectionPropertyExpectedType(type),
    ...STRING_PROPERTY_FILTER_EXPECTED_TYPE
  }
}

/**
 * Creates an expected type configuration for optional properties
 * Optional properties support also 'is set' and 'is not set' operators
 * @param {FilterExpectedType} type - The expected type to create configuration for
 * @returns {FilterOperatorsWithExpectedType} Object mapping FilterOperators to the provided ExpectedType and string operators
 */
export function getOptionalPropertyExpectedType(type: FilterExpectedType): FilterOperatorsWithExpectedType {
  return {
    ...getPropertyExpectedType(type),
    ...FLAG_PROPERTY_FILTER_EXPECTED_TYPE,
    ...STRING_PROPERTY_FILTER_EXPECTED_TYPE
  }
}

/**
 * Creates an expected type configuration for optional properties without string operators
 * Optional properties support 'is set' and 'is not set' operators
 * @param {FilterExpectedType} type - The expected type to create configuration for
 * @returns {FilterOperatorsWithExpectedType} Object mapping FilterOperators to the provided ExpectedType and flag operators
 */
export function getOptionalPropertyExpectedTypeWithoutString(
  type: FilterExpectedType
): FilterOperatorsWithExpectedType {
  return {
    ...getPropertyExpectedType(type),
    ...FLAG_PROPERTY_FILTER_EXPECTED_TYPE
  }
}

/**
 * Creates an expected type configuration for optional collection properties
 * Optional collection properties support also 'is set' and 'is not set' operators
 * @param {FilterExpectedType} type - The expected type to create configuration for
 * @returns {FilterOperatorsWithExpectedType} Object mapping FilterOperators to the provided ExpectedType and string operators
 */
export function getOptionalCollectionPropertyExpectedType(type: FilterExpectedType): FilterOperatorsWithExpectedType {
  return {
    ...getCollectionPropertyExpectedType(type),
    ...FLAG_PROPERTY_FILTER_EXPECTED_TYPE,
    ...STRING_PROPERTY_FILTER_EXPECTED_TYPE
  }
}

/**
 * Creates an expected type configuration for optional collection properties without string operators
 * Optional collection properties support 'is set' and 'is not set' operators
 * @param {FilterExpectedType} type - The expected type to create configuration for
 * @returns {FilterOperatorsWithExpectedType} Object mapping FilterOperators to the provided ExpectedType and flag operators
 */
export function getOptionalCollectionPropertyExpectedTypeWithoutString(
  type: FilterExpectedType
): FilterOperatorsWithExpectedType {
  return {
    ...getCollectionPropertyExpectedType(type),
    ...FLAG_PROPERTY_FILTER_EXPECTED_TYPE
  }
}

/**
 * Gets the filter property configuration for a custom field type property
 * @param {FilterProperty} customFieldFilterProperty - The custom field property type
 * @returns {FilterOperatorsWithExpectedType} The expected type configuration
 */
export function getCustomFieldPropertyExpectedType(
  customFieldFilterProperty: FilterProperty
): FilterOperatorsWithExpectedType {
  switch (customFieldFilterProperty) {
    case FilterProperty.CustomFieldCheckbox:
      return BOOLEAN_PROPERTY_FILTER_EXPECTED_TYPE
    case FilterProperty.CustomFieldNumber:
      return NUMBER_PROPERTY_FILTER_EXPECTED_TYPE
    case FilterProperty.CustomFieldDate:
      return DATE_PROPERTY_FILTER_EXPECTED_TYPE
    case FilterProperty.CustomFieldDateTime:
      return DATE_TIME_PROPERTY_FILTER_EXPECTED_TYPE
    case FilterProperty.CustomFieldText:
    case FilterProperty.CustomFieldLink:
      return STRING_PROPERTY_FILTER_EXPECTED_TYPE
    case FilterProperty.CustomFieldUser:
      return {
        ...getPropertyExpectedType(FilterExpectedType.User),
        ...FLAG_PROPERTY_FILTER_EXPECTED_TYPE
      }
    case FilterProperty.CustomFieldClient:
      return {
        ...getPropertyExpectedType(FilterExpectedType.Company),
        ...FLAG_PROPERTY_FILTER_EXPECTED_TYPE
      }
    case FilterProperty.CustomFieldSelection:
      return SELECTION_FILTER_EXPECTED_TYPE
    default:
      return FLAG_PROPERTY_FILTER_EXPECTED_TYPE
  }
}

export const TASK_FILTER_CONFIGURATION: FilterPropertyConfiguration[] = [
  // task related
  {
    property: FilterProperty.Task,
    expectedType: getRequiredPropertyExpectedType(FilterExpectedType.Task),
    path: '',
    relatedEntity: FilterPropertyEntity.Task,
    allowMultipleValues: true
  },
  {
    property: FilterProperty.TaskIdentifier,
    expectedType: getRequiredPropertyExpectedType(FilterExpectedType.String),
    path: 'taskIdentifier',
    relatedEntity: FilterPropertyEntity.Task
  },
  {
    property: FilterProperty.TaskStatusType,
    expectedType: getPropertyExpectedType(FilterExpectedType.TaskStatusType),
    path: 'taskStatus/type',
    relatedEntity: FilterPropertyEntity.Task,
    allowMultipleValues: true,
    isCompact: true
  },
  {
    property: FilterProperty.TaskStatus,
    expectedType: STRING_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'taskStatus/name',
    relatedEntity: FilterPropertyEntity.Task,
    isCompact: true
  },
  {
    property: FilterProperty.TaskList,
    expectedType: {
      ...STRING_PROPERTY_FILTER_EXPECTED_TYPE,
      ...FLAG_PROPERTY_FILTER_EXPECTED_TYPE
    },
    path: 'lists',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.Task
  },
  {
    property: FilterProperty.TypeOfWork,
    expectedType: getPropertyExpectedType(FilterExpectedType.TypeOfWork),
    path: 'typeOfWork',
    idPath: 'typeOfWorkId',
    relatedEntity: FilterPropertyEntity.Task,
    allowMultipleValues: true,
    isCompact: true
  },
  {
    property: FilterProperty.TaskTag,
    expectedType: getOptionalCollectionPropertyExpectedType(FilterExpectedType.TaskTag),
    path: 'tags',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.Task,
    allowMultipleValues: true
  },
  {
    property: FilterProperty.TaskCreator,
    expectedType: getPropertyExpectedType(FilterExpectedType.User),
    path: 'createdBy',
    relatedEntity: FilterPropertyEntity.Task,
    allowMultipleValues: true,
    computedValues: USER_COMPUTED_VALUES_BY_OPERATOR
  },
  {
    property: FilterProperty.UserAssigned,
    expectedType: getOptionalCollectionPropertyExpectedTypeWithoutString(FilterExpectedType.User),
    path: 'assignees',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.Task,
    allowMultipleValues: true,
    isCompact: true,
    computedValues: USER_COMPUTED_VALUES_BY_OPERATOR
  },
  {
    property: FilterProperty.TaskIsPrio,
    expectedType: BOOLEAN_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'isPrio',
    relatedEntity: FilterPropertyEntity.Task,
    isCompact: true
  },
  {
    property: FilterProperty.TaskIsSubtask,
    expectedType: BOOLEAN_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'isSubtask',
    relatedEntity: FilterPropertyEntity.Task,
    isCompact: true
  },
  {
    property: FilterProperty.TaskCreatedDate,
    expectedType: CREATION_DATE_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'createdOn',
    relatedEntity: FilterPropertyEntity.Task,
    computedValues: DATE_COMPUTED_VALUES_BY_OPERATOR
  },
  {
    property: FilterProperty.StartDate,
    expectedType: DATE_TIME_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'startOn',
    relatedEntity: FilterPropertyEntity.Task,
    computedValues: DATE_COMPUTED_VALUES_BY_OPERATOR
  },
  {
    property: FilterProperty.DueDate,
    expectedType: DATE_TIME_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'dueOn',
    relatedEntity: FilterPropertyEntity.Task,
    isCompact: true,
    computedValues: DATE_COMPUTED_VALUES_BY_OPERATOR
  },
  // project related
  {
    property: FilterProperty.Project,
    expectedType: getOptionalPropertyExpectedType(FilterExpectedType.Project),
    path: 'project',
    idPath: 'projectId',
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true,
    isCompact: true
  },
  {
    property: FilterProperty.ProjectKey,
    expectedType: getRequiredPropertyExpectedType(FilterExpectedType.String),
    path: 'project/projectKey',
    relatedEntity: FilterPropertyEntity.Project
  },
  {
    property: FilterProperty.PrivateProject,
    expectedType: BOOLEAN_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'project/isPrivate',
    relatedEntity: FilterPropertyEntity.Project
  },
  {
    property: FilterProperty.IsExternal,
    expectedType: BOOLEAN_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'isExternal',
    relatedEntity: FilterPropertyEntity.Project,
    isCompact: true
  },
  {
    property: FilterProperty.ProjectType,
    expectedType: getOptionalPropertyExpectedTypeWithoutString(FilterExpectedType.ProjectType),
    path: 'project/projectType',
    idPath: 'project/projectTypeId',
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true
  },
  {
    property: FilterProperty.Workflow,
    expectedType: getOptionalPropertyExpectedTypeWithoutString(FilterExpectedType.Workflow),
    path: 'project/workflowId',
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true,
    isCompact: true
  },
  {
    property: FilterProperty.ProjectStatus,
    expectedType: getRequiredPropertyExpectedType(FilterExpectedType.ProjectStatus),
    path: 'project/projectStatus',
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true
  },
  {
    property: FilterProperty.ProjectStatusType,
    expectedType: getPropertyExpectedType(FilterExpectedType.ProjectStatusType),
    path: 'project/projectStatus/type',
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true
  },
  {
    property: FilterProperty.ProjectMembers,
    expectedType: getOptionalCollectionPropertyExpectedTypeWithoutString(FilterExpectedType.User),
    path: 'project/members',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true,
    isCompact: true,
    computedValues: USER_COMPUTED_VALUES_BY_OPERATOR
  },
  {
    property: FilterProperty.UserResponsible,
    expectedType: getOptionalCollectionPropertyExpectedTypeWithoutString(FilterExpectedType.User),
    path: 'project/members/isResponsible',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true,
    isCompact: true,
    computedValues: USER_COMPUTED_VALUES_BY_OPERATOR
  },
  {
    property: FilterProperty.ProjectTeam,
    expectedType: getOptionalCollectionPropertyExpectedTypeWithoutString(FilterExpectedType.Team),
    path: 'project/teams',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true
  },
  {
    property: FilterProperty.ProjectTag,
    expectedType: getOptionalCollectionPropertyExpectedType(FilterExpectedType.ProjectTag),
    path: 'project/tags',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true
  },
  {
    property: FilterProperty.ProjectCreatedDate,
    expectedType: CREATION_DATE_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'project/createdOn',
    relatedEntity: FilterPropertyEntity.Project,
    computedValues: DATE_COMPUTED_VALUES_BY_OPERATOR
  },
  {
    property: FilterProperty.ProjectStartDate,
    expectedType: DATE_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'project/startDate',
    relatedEntity: FilterPropertyEntity.Project,
    computedValues: DATE_COMPUTED_VALUES_BY_OPERATOR
  },
  {
    property: FilterProperty.ProjectDueDate,
    expectedType: DATE_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'project/dueDate',
    relatedEntity: FilterPropertyEntity.Project,
    computedValues: DATE_COMPUTED_VALUES_BY_OPERATOR
  },
  // user related
  {
    property: FilterProperty.UserTeam,
    expectedType: getOptionalCollectionPropertyExpectedTypeWithoutString(FilterExpectedType.Team),
    path: 'assignees/teams',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.User,
    allowMultipleValues: true
  },
  {
    property: FilterProperty.UserTag,
    expectedType: getOptionalCollectionPropertyExpectedType(FilterExpectedType.UserTag),
    path: 'assignees/tags',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.User,
    allowMultipleValues: true
  },
  // client related
  {
    property: FilterProperty.ProjectClient,
    expectedType: getOptionalPropertyExpectedType(FilterExpectedType.Company),
    path: 'project/company',
    idPath: 'project/companyId',
    relatedEntity: FilterPropertyEntity.Client,
    allowMultipleValues: true
  },
  {
    property: FilterProperty.ClientTag,
    expectedType: getOptionalCollectionPropertyExpectedType(FilterExpectedType.CompanyTag),
    path: 'project/company/tags',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.Client,
    allowMultipleValues: true
  }
]

export const PROJECT_FILTER_CONFIGURATION: FilterPropertyConfiguration[] = [
  {
    property: FilterProperty.Project,
    expectedType: getRequiredPropertyExpectedType(FilterExpectedType.Project),
    path: '',
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true,
    isCompact: true
  },
  {
    property: FilterProperty.ProjectName,
    expectedType: STRING_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'name',
    relatedEntity: FilterPropertyEntity.Project
  },
  {
    property: FilterProperty.ProjectKey,
    expectedType: getRequiredPropertyExpectedType(FilterExpectedType.String),
    path: 'projectKey',
    relatedEntity: FilterPropertyEntity.Project
  },
  {
    property: FilterProperty.PrivateProject,
    expectedType: BOOLEAN_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'isPrivate',
    relatedEntity: FilterPropertyEntity.Project
  },
  {
    property: FilterProperty.IsExternal,
    expectedType: BOOLEAN_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'isExternal',
    relatedEntity: FilterPropertyEntity.Project,
    isCompact: true
  },
  {
    property: FilterProperty.ProjectStatus,
    expectedType: getRequiredPropertyExpectedType(FilterExpectedType.ProjectStatus),
    path: 'projectStatus',
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true
  },
  {
    property: FilterProperty.ProjectStatusType,
    expectedType: getPropertyExpectedType(FilterExpectedType.ProjectStatusType),
    path: 'projectStatus/type',
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true,
    isCompact: true
  },
  {
    property: FilterProperty.ProjectMembers,
    expectedType: getOptionalCollectionPropertyExpectedTypeWithoutString(FilterExpectedType.User),
    path: 'members',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true,
    isCompact: true,
    computedValues: USER_COMPUTED_VALUES_BY_OPERATOR
  },
  {
    property: FilterProperty.UserResponsible,
    expectedType: getOptionalCollectionPropertyExpectedTypeWithoutString(FilterExpectedType.User),
    path: 'members/isResponsible',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true,
    isCompact: true,
    computedValues: USER_COMPUTED_VALUES_BY_OPERATOR
  },
  {
    property: FilterProperty.ProjectTeam,
    expectedType: getOptionalCollectionPropertyExpectedTypeWithoutString(FilterExpectedType.Team),
    path: 'teams',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true,
    isCompact: true
  },
  {
    property: FilterProperty.ProjectType,
    expectedType: getOptionalPropertyExpectedTypeWithoutString(FilterExpectedType.ProjectType),
    path: 'projectType',
    idPath: 'projectTypeId',
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true,
    isCompact: true
  },
  {
    property: FilterProperty.Workflow,
    expectedType: getOptionalPropertyExpectedTypeWithoutString(FilterExpectedType.Workflow),
    path: 'workflowId',
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true,
    isCompact: true
  },
  {
    property: FilterProperty.ProjectTag,
    expectedType: getOptionalCollectionPropertyExpectedType(FilterExpectedType.ProjectTag),
    path: 'tags',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.Project,
    allowMultipleValues: true
  },
  {
    property: FilterProperty.StartDate,
    expectedType: DATE_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'startDate',
    relatedEntity: FilterPropertyEntity.Project,
    computedValues: DATE_COMPUTED_VALUES_BY_OPERATOR
  },
  {
    property: FilterProperty.DueDate,
    expectedType: DATE_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'dueDate',
    relatedEntity: FilterPropertyEntity.Project,
    isCompact: true,
    computedValues: DATE_COMPUTED_VALUES_BY_OPERATOR
  },
  {
    property: FilterProperty.ProjectClient,
    expectedType: getOptionalPropertyExpectedType(FilterExpectedType.Company),
    path: 'company',
    idPath: 'companyId',
    relatedEntity: FilterPropertyEntity.Client,
    allowMultipleValues: true,
    isCompact: true
  }
]

export const USER_FILTER_CONFIGURATION: FilterPropertyConfiguration[] = [
  {
    property: FilterProperty.User,
    expectedType: getRequiredPropertyExpectedType(FilterExpectedType.User),
    path: '',
    relatedEntity: FilterPropertyEntity.User,
    allowMultipleValues: true,
    isCompact: true
  },
  {
    property: FilterProperty.UserFirstName,
    expectedType: STRING_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'firstName',
    relatedEntity: FilterPropertyEntity.User,
    isCompact: true
  },
  {
    property: FilterProperty.UserLastName,
    expectedType: STRING_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'lastName',
    relatedEntity: FilterPropertyEntity.User,
    isCompact: true
  },
  {
    property: FilterProperty.UserEmail,
    expectedType: STRING_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'email',
    relatedEntity: FilterPropertyEntity.User,
    isCompact: true
  },
  {
    property: FilterProperty.UserStatus,
    expectedType: getPropertyExpectedType(FilterExpectedType.UserStatus),
    path: 'status',
    relatedEntity: FilterPropertyEntity.User,
    allowMultipleValues: true,
    isCompact: true
  },
  {
    property: FilterProperty.IsExternal,
    expectedType: BOOLEAN_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'isExternal',
    relatedEntity: FilterPropertyEntity.User,
    isCompact: true
  },
  {
    property: FilterProperty.UserPosition,
    expectedType: STRING_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'position',
    relatedEntity: FilterPropertyEntity.User,
    isCompact: true
  },
  {
    property: FilterProperty.UserTeam,
    expectedType: getOptionalCollectionPropertyExpectedTypeWithoutString(FilterExpectedType.Team),
    path: 'teams',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.User,
    allowMultipleValues: true,
    isCompact: true
  },
  {
    property: FilterProperty.UserTag,
    expectedType: getOptionalCollectionPropertyExpectedType(FilterExpectedType.UserTag),
    path: 'tags',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.User,
    allowMultipleValues: true,
    isCompact: true
  }
]

export const COMPANY_FILTER_CONFIGURATION: FilterPropertyConfiguration[] = [
  {
    property: FilterProperty.Company,
    expectedType: getRequiredPropertyExpectedType(FilterExpectedType.Company),
    path: '',
    relatedEntity: FilterPropertyEntity.Client,
    allowMultipleValues: true,
    isCompact: true
  },
  {
    property: FilterProperty.CompanyAddress,
    expectedType: STRING_PROPERTY_FILTER_EXPECTED_TYPE,
    path: 'address',
    relatedEntity: FilterPropertyEntity.Client,
    isCompact: true
  },
  {
    property: FilterProperty.ClientTag,
    expectedType: getOptionalCollectionPropertyExpectedType(FilterExpectedType.CompanyTag),
    path: 'tags',
    isCollection: true,
    relatedEntity: FilterPropertyEntity.Client,
    allowMultipleValues: true,
    isCompact: true
  }
]

export const FILTER_CONFIGURATION: Record<FilterEntityType, FilterPropertyConfiguration[]> = {
  [FilterEntityType.Task]: TASK_FILTER_CONFIGURATION,
  [FilterEntityType.Project]: PROJECT_FILTER_CONFIGURATION,
  [FilterEntityType.User]: USER_FILTER_CONFIGURATION,
  [FilterEntityType.Company]: COMPANY_FILTER_CONFIGURATION
}

export const FILTER_PROPERTY_ICONS: Record<FilterProperty, string> = {
  [FilterProperty.Project]: 'folder',
  [FilterProperty.Workflow]: 'account_tree',
  [FilterProperty.ProjectType]: 'category',
  [FilterProperty.PrivateProject]: 'lock',
  [FilterProperty.ProjectStatus]: 'check_circle',
  [FilterProperty.ProjectStatusType]: 'check_circle',
  [FilterProperty.ProjectClient]: 'business',
  [FilterProperty.ClientTag]: 'local_offer',
  [FilterProperty.ProjectTeam]: 'group',
  [FilterProperty.ProjectTag]: 'local_offer',
  [FilterProperty.ProjectStartDate]: 'event',
  [FilterProperty.ProjectCreatedDate]: 'event',
  [FilterProperty.TaskCreatedDate]: 'event',
  [FilterProperty.ProjectDueDate]: 'alarm',
  [FilterProperty.User]: 'person',
  [FilterProperty.UserAssigned]: 'person',
  [FilterProperty.UserTeam]: 'group',
  [FilterProperty.UserTag]: 'local_offer',
  [FilterProperty.StartDate]: 'event',
  [FilterProperty.DueDate]: 'event',
  [FilterProperty.Task]: 'task',
  [FilterProperty.TaskIdentifier]: 'tag',
  [FilterProperty.TypeOfWork]: 'category',
  [FilterProperty.TaskStatus]: 'check_circle',
  [FilterProperty.TaskStatusType]: 'check_circle',
  [FilterProperty.TaskTag]: 'local_offer',
  [FilterProperty.TaskList]: 'list',
  [FilterProperty.TaskCreator]: 'person',
  [FilterProperty.TaskIsPrio]: 'flag',
  [FilterProperty.TaskIsSubtask]: 'subtasks',
  [FilterProperty.ProjectName]: 'text_fields',
  [FilterProperty.ProjectKey]: 'vpn_key',
  [FilterProperty.UserResponsible]: 'person',
  [FilterProperty.ProjectMembers]: 'group',
  [FilterProperty.UserFirstName]: 'text_fields',
  [FilterProperty.UserLastName]: 'text_fields',
  [FilterProperty.UserEmail]: 'alternate_email',
  [FilterProperty.UserPosition]: 'account_circle',
  [FilterProperty.UserStatus]: 'account_circle',
  [FilterProperty.Company]: 'business',
  [FilterProperty.CompanyAddress]: 'business',
  [FilterProperty.IsExternal]: 'public',
  [FilterProperty.CustomFieldCheckbox]: 'toggle_on',
  [FilterProperty.CustomFieldLink]: 'link',
  [FilterProperty.CustomFieldNumber]: 'tag',
  [FilterProperty.CustomFieldSelection]: 'list',
  [FilterProperty.CustomFieldUser]: 'person',
  [FilterProperty.CustomFieldClient]: 'business',
  [FilterProperty.CustomFieldText]: 'text_fields',
  [FilterProperty.CustomFieldDate]: 'today',
  [FilterProperty.CustomFieldDateTime]: 'today',
  [FilterProperty.Prompt]: 'awork'
}
